import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import {
  AddMfaBackup,
  CancelAuthentication,
  ContinueAuthentication,
  DeviceType,
  FlyDeltaPoll,
  InitiateAuthentication,
  InitiateSsoSession,
  LoginOAuthParams,
  SubmitEmailOtp,
  SubmitSmsNumber,
  SubmitSmsOtp,
  SwapAuthForAccessTokens
} from '../../../models';
import { ExistingUserMfaWrapperHelperService } from './existing-user-mfa-wrapper-helper.service';

@Injectable({
  providedIn: 'root'
})
export class ExistingUserMfaWrapperService extends ExistingUserMfaWrapperHelperService {
  existingUserMfaFlow(
    loginOAuthParams: LoginOAuthParams,
    deviceType?: DeviceType,
    otp?: string,
    phoneNumber?: string,
    retry?: boolean
  ): Observable<
    | InitiateSsoSession
    | InitiateAuthentication
    | SubmitEmailOtp
    | ContinueAuthentication
    | AddMfaBackup
    | SubmitSmsNumber
    | CancelAuthentication
    | FlyDeltaPoll
    | SwapAuthForAccessTokens
    | SubmitSmsOtp
    | string
  > {
    switch (deviceType) {
      case 'Email':
        return this.emailHandler(loginOAuthParams, otp);
      case 'SMS':
        return this.smsMethod.initSMS(
          loginOAuthParams,
          this.flowId,
          otp,
          phoneNumber,
          retry
        );
      case 'MOBILE':
        return this.flyDeltaMethod.initFlyDelta(
          loginOAuthParams,
          this.flowId,
          this.applicationName,
          this.applicationId
        );
      default: {
        const swapResponse = this.finish(loginOAuthParams);
        return swapResponse;
      }
    }
  }

  emailHandler(
    loginOAuthParams: LoginOAuthParams,
    otp?: string
  ): Observable<string | InitiateAuthentication | AddMfaBackup> {
    if (!otp) {
      return this.initiateMfaWithEmailValidation(loginOAuthParams);
    } else {
      return this.verifyMfaEmail(loginOAuthParams, this.flowId, otp);
    }
  }

  initiateMfaWithEmailValidation(
    loginOAuthParams: LoginOAuthParams
  ): Observable<string | InitiateAuthentication> {
    return this.initiateSsoSession
      .initiateSsoSession(loginOAuthParams, 'DEFAULT')
      .pipe(
        concatMap((response: InitiateSsoSession) => {
          if (response && response.id) {
            this.flowId = response.id;
            this.multiFactorAuthenticationUtils.flowId = this.flowId;
          }
          return this.initiateAuthentication.initiateAuthentication(
            loginOAuthParams.oauthConfig,
            response?.id ? response.id : ''
          );
        }),
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }

  verifyMfaEmail(
    loginOAuthParams: LoginOAuthParams,
    flowId: string,
    otp: string
  ): Observable<string | AddMfaBackup> {
    return this.submitEmailOtp
      .submitEmailOtp(loginOAuthParams.oauthConfig, flowId, otp)
      .pipe(
        concatMap((response: SubmitEmailOtp) => {
          return this.continueAuthentication.continueAuthentication(
            loginOAuthParams.oauthConfig,
            response?.id ? response.id : ''
          );
        }),
        concatMap((response: ContinueAuthentication) => {
          return this.initiateAuthentication.initiateAuthentication(
            loginOAuthParams.oauthConfig,
            response?.id ? response.id : ''
          );
        }),
        concatMap((response: InitiateAuthentication) => {
          return this.addBackupMfa.addBackupMfa(
            loginOAuthParams.oauthConfig,
            response?.id ? response.id : ''
          );
        }),
        tap((res: AddMfaBackup) => {
          this.applicationId =
            res.devicePairingMethods?.find(
              (device) =>
                device.deviceType === 'MOBILE' &&
                device.applicationName ===
                  loginOAuthParams.oauthConfig.FLY_DELTA_POLLING_APP_NAME
            )?.applicationId ?? '';
          this.applicationName =
            res.devicePairingMethods?.find(
              (device) =>
                device.deviceType === 'MOBILE' &&
                device.applicationName ===
                  loginOAuthParams.oauthConfig.FLY_DELTA_POLLING_APP_NAME
            )?.applicationName ?? '';
        }),
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }
}
